import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Section, useToggle } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { PerformanceReviewsIndexPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import users from 'modules/core/store/collections/users'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import CompetenciesDiscoveryModal from 'modules/job_catalog/components/CompetenciesDiscoveryModal'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { ChooseNewReviewModal } from 'modules/reviews/components/ChooseNewReviewModal'
import ReviewsDataTable from 'modules/reviews/components/ReviewsDataTable'

function ReviewsIndexPage() {
  const [hideDiscoveryModal, setHideDiscoveryModal] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideCompetenciesDiscoveryModalTip
  )
  const { data, loading: loadingQueryData, error } = useQuery(PerformanceReviewsIndexPageDocument)
  const [isOpen, toggle] = useToggle(!hideDiscoveryModal)
  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const canManageCompetencies = users.current.policy.can('competencies.competenciesManage')
  const isRoleEditor = jobRolesCollection.some((x) =>
    policyAuthorizations.for('job_catalog.edit_job', x)
  )

  const [showNewReviewModal, setShowNewReviewModal] = useState(false)
  const showCompetenciesModal = isOpen && (canCreateJob || isRoleEditor || canManageCompetencies)

  const { navigation } = useNavigation()

  const canCreateReviews = data?.apiCore?._policies?.accessReviewsCreateForms.can
  const canCreateTemplates = data?.apiCore?._policies?.accessReviewsCreateTemplates.can

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        policyAuthorizations.authorizePolicies('job_catalog.edit_job'),
        contractVersions.fetch({ withCachedFetchParams: true }),
      ]),
    []
  )

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading || loadingQueryData}
      error={error}
      actions={
        canCreateTemplates
          ? {
              primary: [
                {
                  icon: 'settings',
                  to: navigation.settings.performance_reviews.fullPath(),
                  variant: 'secondary',
                  label: '',
                },
              ],
            }
          : undefined
      }
    >
      <Section.FullWidth
        illustration="surveys"
        title={i18n.t('surveys.performance.index.title')}
        description={i18n.t('surveys.performance.index.description')}
        size="600"
      >
        <ReviewsDataTable
          onAddNewReview={canCreateReviews ? () => setShowNewReviewModal(true) : undefined}
        />
        <Outlet />
      </Section.FullWidth>
      {showNewReviewModal && (
        <ChooseNewReviewModal
          onClose={() => setShowNewReviewModal(false)}
          contractVersions={contractVersions}
        />
      )}
      {showCompetenciesModal && (
        <CompetenciesDiscoveryModal
          onClose={() => {
            setHideDiscoveryModal(true)
            toggle(false)()
          }}
          handleOnComplete={() => setHideDiscoveryModal(true)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(ReviewsIndexPage)
